import { existsSync, readFileSync } from "node:fs";
import { writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";
import type { Config } from "../types";
import { resolvePathAlias } from "./path-resolver";

const DEFAULT_CONFIG: Config = {
  output: "@/components/icons",
  typescript: true,
  extension: "tsx",
  indexFile: true,
};

/**
 * Normalize config to ensure extension matches typescript setting
 * and resolve path aliases
 */
function normalizeConfig(config: Config): Config {
  // Ensure extension matches typescript setting if not explicitly set
  if (config.typescript && config.extension === "jsx") {
    config.extension = "tsx";
  } else if (!config.typescript && config.extension === "tsx") {
    config.extension = "jsx";
  }

  // Resolve path aliases in output path
  config.output = resolvePathAlias(config.output);

  return config;
}

const CONFIG_FILE_NAME = ".pixelarticonsrc.json";

/**
 * Find config file by searching upward from current directory
 */
function findConfigFile(startDir: string = process.cwd()): string | null {
  let currentDir = startDir;

  while (true) {
    const configPath = join(currentDir, CONFIG_FILE_NAME);
    if (existsSync(configPath)) {
      return configPath;
    }

    const parentDir = dirname(currentDir);
    if (parentDir === currentDir) {
      // Reached root
      return null;
    }
    currentDir = parentDir;
  }
}

/**
 * Load configuration from environment variables
 */
function loadEnvConfig(): Partial<Config> {
  const envConfig: Partial<Config> = {};

  if (process.env.PIXELARTICONS_OUTPUT) {
    envConfig.output = process.env.PIXELARTICONS_OUTPUT;
  }

  if (process.env.PIXELARTICONS_TYPESCRIPT) {
    envConfig.typescript = process.env.PIXELARTICONS_TYPESCRIPT === "true";
  }

  if (process.env.PIXELARTICONS_EXTENSION) {
    const ext = process.env.PIXELARTICONS_EXTENSION;
    if (ext === "tsx" || ext === "jsx") {
      envConfig.extension = ext;
    }
  }

  if (process.env.PIXELARTICONS_INDEX_FILE) {
    envConfig.indexFile = process.env.PIXELARTICONS_INDEX_FILE === "true";
  }

  return envConfig;
}

/**
 * Load and parse config file with environment variable support
 */
export function loadConfig(configPath?: string): Config {
  const foundPath = configPath || findConfigFile();

  let fileConfig: Partial<Config> = {};

  if (foundPath) {
    try {
      const content = readFileSync(foundPath, "utf-8");
      const parsed = JSON.parse(content);
      fileConfig = parsed;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(
          `Invalid config file: JSON parse error - ${error.message}`,
        );
      }
      throw error;
    }
  }

  // Merge with priority: defaults -> file -> env vars
  const envConfig = loadEnvConfig();

  const mergedConfig = {
    ...DEFAULT_CONFIG,
    ...fileConfig,
    ...envConfig,
  };

  return normalizeConfig(mergedConfig);
}

/**
 * Create config file with default or custom values
 */
export async function createConfigFile(
  dir: string,
  customConfig?: Partial<Config>,
  force: boolean = false,
): Promise<string> {
  const configPath = join(dir, CONFIG_FILE_NAME);

  if (existsSync(configPath) && !force) {
    throw new Error("Config file already exists");
  }

  const config = {
    ...DEFAULT_CONFIG,
    ...customConfig,
  };

  await writeFile(configPath, `${JSON.stringify(config, null, 2)}\n`, "utf-8");

  return configPath;
}

/**
 * Validate config values
 */
export function validateConfig(config: Config): void {
  if (!config.output || typeof config.output !== "string") {
    throw new Error("output must be a non-empty string");
  }

  if (typeof config.typescript !== "boolean") {
    throw new Error("typescript must be a boolean (true or false)");
  }

  if (!["tsx", "jsx"].includes(config.extension)) {
    throw new Error('extension must be "tsx" or "jsx"');
  }

  if (typeof config.indexFile !== "boolean") {
    throw new Error("indexFile must be a boolean (true or false)");
  }
}

export { DEFAULT_CONFIG, CONFIG_FILE_NAME };
